import { useState, useEffect } from "react";
import { Modal, Form, Button, Row, Col, Alert } from "react-bootstrap";
import { toast } from "react-toastify";
import Select from "react-select";
import { getTaskById, updateTask } from "../Api/TaskMaster";
import { getTeamById } from "../Api/TeamMaster";
import { getUsersByIds } from "../Api/Auth";
import { fetchProjectDetails } from "../Api/ViewProjects";
import { fetchStagesByProject } from "../Api/StageMaster";
import {
  getBuildingBlocksByOrgAndProject,
  mapTaskToBuildingBlock,
  getBuildingBlocksByTask,
  removeSpecificTaskMapping,
} from "../Api/BuildingBlockMaster";
import { getAllTaskStatus } from "../Api/TaskMaster";
import { getPartnersByProject } from "../Api/PartnerMaster";
import { fetchCusVenCpByCusVenType } from "../Api/CusVenCpMasterApi";
import { getAllTaskTypes } from "../Api/TaskMaster";

const TaskEditModal = ({ show, onHide, taskId, onTaskUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [taskData, setTaskData] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamMemberDetails, setTeamMemberDetails] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [stages, setStages] = useState([]);
  const [loadingStages, setLoadingStages] = useState(false);

  const [buildingBlocks, setBuildingBlocks] = useState([]);
  const [loadingBlocks, setLoadingBlocks] = useState(false);
  const [selectedBlockIds, setSelectedBlockIds] = useState([]);
  const [existingBlockIds, setExistingBlockIds] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(false);

  const [taskTypes, setTaskTypes] = useState([]);
  const [loadingTaskTypes, setLoadingTaskTypes] = useState(false);

  // New state for contact persons
  const [clientContactPersons, setClientContactPersons] = useState([]);
  const [partnerContactPersons, setPartnerContactPersons] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [clientId, setClientId] = useState(null);
  const [projectPartners, setProjectPartners] = useState([]);

  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const username = userDetails?.username;
  const orgId = userDetails?.organisationId;

  // Fetch contact persons for client
  const fetchClientContactPersons = async (clientId) => {
    if (!clientId) return [];
    try {
      console.log("Fetching client contacts for:", clientId);
      const contacts = await fetchCusVenCpByCusVenType(clientId);
      if (Array.isArray(contacts)) {
        console.log("Client contacts found:", contacts);
        setClientContactPersons(contacts);
        return contacts;
      } else {
        setClientContactPersons([]);
        return [];
      }
    } catch (error) {
      console.error("Error fetching client contact persons:", error);
      setClientContactPersons([]);
      return [];
    }
  };

  const loadTaskTypes = async () => {
    try {
      setLoadingTaskTypes(true);

      const data = await getAllTaskTypes();

      const sorted = data.sort((a, b) => a.typeName.localeCompare(b.typeName));

      setTaskTypes(sorted);
    } catch (error) {
      console.error("Error loading task types:", error);
      toast.error("Failed to load task types");
    } finally {
      setLoadingTaskTypes(false);
    }
  };

  // Fetch contact persons for partners
  const fetchPartnerContactPersons = async (partners) => {
    if (!partners || partners.length === 0) return [];

    try {
      setLoadingContacts(true);
      const allPartnerContacts = [];
      for (const partner of partners) {
        const partnerId = partner.partnerName; // partnerName contains the vendor code
        console.log("Fetching partner contacts for:", partnerId);
        const contacts = await fetchCusVenCpByCusVenType(partnerId);
        if (Array.isArray(contacts) && contacts.length > 0) {
          console.log(
            `Found ${contacts.length} contacts for partner ${partnerId}`,
          );
          allPartnerContacts.push(...contacts);
        }
      }
      console.log("Total partner contacts found:", allPartnerContacts.length);
      setPartnerContactPersons(allPartnerContacts);
      return allPartnerContacts;
    } catch (error) {
      console.error("Error fetching partner contact persons:", error);
      setPartnerContactPersons([]);
      return [];
    } finally {
      setLoadingContacts(false);
    }
  };

  // Fetch user details for team members
  const fetchUserDetails = async (userIds) => {
    if (!userIds || userIds.length === 0) {
      setTeamMemberDetails([]);
      return;
    }
    try {
      const numericUserIds = userIds
        .map((id) => parseInt(id))
        .filter((id) => !isNaN(id));
      if (numericUserIds.length === 0) {
        setTeamMemberDetails([]);
        return;
      }
      const response = await getUsersByIds(numericUserIds);
      if (response && response.users) {
        setTeamMemberDetails(response.users);
      } else {
        setTeamMemberDetails([]);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      setTeamMemberDetails([]);
    }
  };

  const loadTaskStatus = async () => {
    try {
      setLoadingStatus(true);
      const data = await getAllTaskStatus();
      const sorted = data.sort((a, b) =>
        a.statusName.localeCompare(b.statusName),
      );
      setStatusList(sorted);
    } catch (error) {
      console.error("Error loading status:", error);
      toast.error("Failed to load status");
    } finally {
      setLoadingStatus(false);
    }
  };

  // Fetch team members when project is available
  const fetchTeamMembers = async (projectId) => {
    if (!projectId) return;
    try {
      setLoadingTeam(true);
      // First get project details to get the team ID
      const projectData = await fetchProjectDetails(projectId);
      console.log("Fetched project data:", projectData);

      // Store client ID for reference
      if (projectData && projectData.prClient) {
        setClientId(projectData.prClient);
      }

      // Fetch team members
      if (projectData && projectData.teamAssigned) {
        const teamData = await getTeamById(projectData.teamAssigned);
        if (teamData && teamData.teamMembers) {
          setTeamMembers(teamData.teamMembers);
          await fetchUserDetails(teamData.teamMembers);
        } else {
          setTeamMembers([]);
          setTeamMemberDetails([]);
        }
      } else {
        setTeamMembers([]);
        setTeamMemberDetails([]);
      }

      // Fetch client contact persons
      if (projectData && projectData.prClient) {
        await fetchClientContactPersons(projectData.prClient);
      }

      // Fetch partner contact persons
      try {
        const partners = await getPartnersByProject(projectId);
        console.log("Project partners:", partners);
        if (Array.isArray(partners) && partners.length > 0) {
          setProjectPartners(partners);
          await fetchPartnerContactPersons(partners);
        } else {
          setProjectPartners([]);
          setPartnerContactPersons([]);
        }
      } catch (error) {
        console.error("Error fetching partners:", error);
        setProjectPartners([]);
        setPartnerContactPersons([]);
      }
    } catch (error) {
      console.error("Error fetching team members:", error);
      setTeamMembers([]);
      setTeamMemberDetails([]);
    } finally {
      setLoadingTeam(false);
    }
  };

  // Get user display name similar to TaskMaster
  const getUserDisplayName = (memberId) => {
    const userDetail = teamMemberDetails.find(
      (user) => user.userId === parseInt(memberId),
    );
    if (userDetail) {
      return `${userDetail.username} (${userDetail.email})`;
    }
    return memberId; // Fallback to ID if user details not found
  };

  const fetchProjectStages = async (projectId) => {
    if (!projectId) return;
    try {
      setLoadingStages(true);
      const stageData = await fetchStagesByProject(projectId);
      if (Array.isArray(stageData)) {
        setStages(stageData);
      } else {
        setStages([]);
      }
    } catch (error) {
      console.error("Error fetching stages:", error);
      setStages([]);
    } finally {
      setLoadingStages(false);
    }
  };

  // Fetch task details when modal opens
  useEffect(() => {
    if (show && taskId) {
      loadTaskDetails();
    }
  }, [show, taskId]);

  useEffect(() => {
    if (show) {
      loadTaskTypes();
    }
  }, [show]);

  const buildingBlockOptions = buildingBlocks.map((block) => ({
    value: block.buildingBlockId,
    label: `${block.buildingBlockName} (${block.buildingBlockId})`,
  }));

  const loadTaskDetails = async () => {
    setLoading(true);
    try {
      const taskDetails = await getTaskById(taskId);
      setTaskData(taskDetails);

      // Fetch team members, contact persons, and stages for the project
      if (taskDetails.projectBelong) {
        await fetchTeamMembers(taskDetails.projectBelong);
        await fetchProjectStages(taskDetails.projectBelong);
      }

      // Load building blocks for project
      try {
        setLoadingBlocks(true);
        const blocks = await getBuildingBlocksByOrgAndProject(
          userDetails.organisationId,
          taskDetails.projectBelong,
        );
        setBuildingBlocks(blocks || []);

        // Get currently mapped block
        const mappedBlocks = await getBuildingBlocksByTask(taskId);
        setExistingBlockIds(mappedBlocks || []);
        setSelectedBlockIds(mappedBlocks || []);
      } catch (err) {
        console.error("Failed to load building blocks", err);
      } finally {
        setLoadingBlocks(false);
      }
    } catch (error) {
      console.error("Error loading task details:", error);
      toast.error("Failed to load task details");
      onHide();
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setTaskData({
      ...taskData,
      [e.target.name]: e.target.value,
    });
  };

  const handleDateChange = (e) => {
    setTaskData({
      ...taskData,
      [e.target.name]: e.target.value ? new Date(e.target.value) : null,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!taskData) return;

    setSaving(true);
    try {
      const updateData = {
        TaskName: taskData.taskName,
        ProjectBelong: taskData.projectBelong,
        AssignedTo: taskData.assignedTo,
        TaskStartDate: taskData.taskStartDate,
        TaskEndDate: taskData.taskEndDate,
        TaskType: taskData.taskType,
        TaskPriority: taskData.taskPriority,
        TaskStatus: taskData.taskStatus,
        TaskDescription: taskData.taskDescription,
        ActualStartDate: taskData.actualStartDate,
        ActualEndDate: taskData.actualEndDate,
        EditedBy: username,
        StageBelong: taskData.stageBelong || null,
      };

      await updateTask(taskId, updateData);

      // Handle Building Block Mapping (Many-to-Many)
      try {
        const blocksToAdd = selectedBlockIds.filter(
          (id) => !existingBlockIds.includes(id),
        );
        const blocksToRemove = existingBlockIds.filter(
          (id) => !selectedBlockIds.includes(id),
        );

        for (const blockId of blocksToAdd) {
          await mapTaskToBuildingBlock({
            buildingBlockId: blockId,
            taskId: taskId,
            orgId: String(userDetails.organisationId),
            createdBy: String(userDetails.userId),
          });
        }

        for (const blockId of blocksToRemove) {
          await removeSpecificTaskMapping(taskId, blockId);
        }

        setExistingBlockIds([...selectedBlockIds]);
      } catch (err) {
        console.error("Failed updating building block mappings", err);
      }

      toast.success("Task updated successfully");
      onTaskUpdated();
      onHide();
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (show) {
      loadTaskStatus();
    }
  }, [show]);

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  };

  // Function to get stage display name
  const getStageDisplayName = (stageId) => {
    if (!stageId) return "No Stage";
    const stage = stages.find((s) => s.stageId === stageId);
    return stage ? `${stage.stageName} (${stage.stageId})` : stageId;
  };

  // Combine all assignee options
  const getAllAssigneeOptions = () => {
    const options = [];
    const seenValues = new Set(); // To avoid duplicates

    // Add team members
    teamMembers.forEach((memberId) => {
      const userDetail = teamMemberDetails.find(
        (user) => user.userId === parseInt(memberId),
      );
      if (userDetail && !seenValues.has(memberId)) {
        seenValues.add(memberId);
        options.push({
          value: memberId,
          label: `${userDetail.username} (${userDetail.email}) - Team Member`,
          type: "team",
        });
      } else if (!seenValues.has(memberId)) {
        seenValues.add(memberId);
        options.push({
          value: memberId,
          label: `${memberId} - Team Member`,
          type: "team",
        });
      }
    });

    // Add client contact persons
    clientContactPersons.forEach((contact) => {
      if (!seenValues.has(contact.cusVenId)) {
        seenValues.add(contact.cusVenId);
        options.push({
          value: contact.cusVenId,
          label: `${contact.contactPerName} (${contact.contactPerEmail || "No email"}) - Client Contact`,
          type: "client",
        });
      }
    });

    // Add partner contact persons
    partnerContactPersons.forEach((contact) => {
      if (!seenValues.has(contact.cusVenId)) {
        seenValues.add(contact.cusVenId);
        options.push({
          value: contact.cusVenId,
          label: `${contact.contactPerName} (${contact.contactPerEmail || "No email"}) - Partner Contact`,
          type: "partner",
        });
      }
    });

    // Sort options by type and then by name
    options.sort((a, b) => {
      if (a.type !== b.type) {
        const typeOrder = { team: 1, client: 2, partner: 3 };
        return typeOrder[a.type] - typeOrder[b.type];
      }
      return a.label.localeCompare(b.label);
    });

    console.log("Total assignee options:", options.length);
    console.log("Options breakdown:", {
      team: options.filter((o) => o.type === "team").length,
      client: options.filter((o) => o.type === "client").length,
      partner: options.filter((o) => o.type === "partner").length,
    });

    return options;
  };

  if (!show) return null;

  const assigneeOptions = getAllAssigneeOptions();
  const isLoading = loadingTeam || loadingContacts;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          <i className="fas fa-edit me-2"></i>
          Edit Task
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading task details...</span>
              </div>
              <p className="mt-2">Loading task details...</p>
            </div>
          ) : !taskData ? (
            <Alert variant="danger">Failed to load task details</Alert>
          ) : (
            <>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Task Name <span className="text-red">**</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="taskName"
                      value={taskData.taskName || ""}
                      onChange={handleChange}
                      required
                      placeholder="Enter task name"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Assigned To</Form.Label>
                    {isLoading ? (
                      <Form.Control
                        type="text"
                        disabled
                        value="Loading assignees..."
                        className="bg-light"
                      />
                    ) : (
                      <Form.Select
                        name="assignedTo"
                        value={taskData.assignedTo || ""}
                        onChange={handleChange}
                      >
                        <option value="">Select Assignee</option>
                        {assigneeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                    <Form.Text
                      className="text-muted"
                      style={{ fontSize: "11px" }}
                    >
                      {assigneeOptions.length > 0
                        ? `Available assignees: ${assigneeOptions.length} total (${assigneeOptions.filter((o) => o.type === "team").length} team members, ${assigneeOptions.filter((o) => o.type === "client").length} client contacts, ${assigneeOptions.filter((o) => o.type === "partner").length} partner contacts)`
                        : "No assignees available. Please ensure the project has team members, client contacts, or partner contacts."}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Task Type <span className="text-red">**</span>
                    </Form.Label>
                    <Form.Select
                      name="taskType"
                      value={taskData.taskType || ""}
                      onChange={handleChange}
                      required
                    >
                      <option value="">
                        {loadingTaskTypes ? "Loading..." : "Select Task Type"}
                      </option>

                      {taskTypes.map((type) => (
                        <option key={type.typeGuid} value={type.typeGuid}>
                          {type.typeName}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Priority <span className="text-red">**</span>
                    </Form.Label>
                    <Form.Select
                      name="taskPriority"
                      value={taskData.taskPriority || ""}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Priority</option>
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Status <span className="text-red">**</span>
                    </Form.Label>
                    <Form.Select
                      name="taskStatus"
                      value={taskData.taskStatus || ""}
                      onChange={handleChange}
                      required
                    >
                      <option value="">
                        {loadingStatus ? "Loading..." : "Select Status"}
                      </option>
                      {statusList.map((status) => (
                        <option
                          key={status.statusGuid}
                          value={status.statusGuid}
                        >
                          {status.statusName}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Stage</Form.Label>
                    {loadingStages ? (
                      <Form.Control
                        type="text"
                        disabled
                        value="Loading stages..."
                        className="bg-light"
                      />
                    ) : (
                      <Form.Select
                        name="stageBelong"
                        value={taskData.stageBelong || ""}
                        onChange={handleChange}
                      >
                        <option value="">Select Stage (Optional)</option>
                        {stages.map((stage) => (
                          <option key={stage.stageId} value={stage.stageId}>
                            {stage.stageName} ({stage.stageId})
                          </option>
                        ))}
                      </Form.Select>
                    )}
                    {stages.length === 0 && !loadingStages && (
                      <Form.Text
                        className="text-muted"
                        style={{ fontSize: "11px" }}
                      >
                        No stages available for this project
                      </Form.Text>
                    )}
                    {taskData.stageBelong && (
                      <Form.Text
                        className="text-info"
                        style={{ fontSize: "11px" }}
                      >
                        Current stage:{" "}
                        {getStageDisplayName(taskData.stageBelong)}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Planned Start Date <span className="text-red">**</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="taskStartDate"
                      value={formatDateForInput(taskData.taskStartDate)}
                      onChange={handleDateChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Planned End Date <span className="text-red">**</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="taskEndDate"
                      value={formatDateForInput(taskData.taskEndDate)}
                      onChange={handleDateChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Actual Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="actualStartDate"
                      value={formatDateForInput(taskData.actualStartDate)}
                      onChange={handleDateChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Actual End Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="actualEndDate"
                      value={formatDateForInput(taskData.actualEndDate)}
                      onChange={handleDateChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Building Block</Form.Label>
                    {loadingBlocks ? (
                      <Form.Control
                        type="text"
                        disabled
                        value="Loading building blocks..."
                        className="bg-light"
                      />
                    ) : (
                      <Select
                        isMulti
                        options={buildingBlockOptions}
                        value={buildingBlockOptions.filter((opt) =>
                          selectedBlockIds.includes(opt.value),
                        )}
                        onChange={(selectedOptions) => {
                          const values = selectedOptions
                            ? selectedOptions.map((opt) => opt.value)
                            : [];
                          setSelectedBlockIds(values);
                        }}
                        placeholder="Select Building Blocks..."
                      />
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Task Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="taskDescription"
                      value={taskData.taskDescription || ""}
                      onChange={handleChange}
                      placeholder="Enter detailed task description..."
                      maxLength={1000}
                    />
                    <Form.Text className="text-muted">
                      {taskData.taskDescription?.length || 0}/1000 characters
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={saving || loading || !taskData}
          >
            {saving ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default TaskEditModal;
